/**
 * The device database (IndexedDB via Dexie). Offline work must survive app
 * updates, so the schema is versioned and only ever moves forward.
 *
 * HOW TO CHANGE THE SCHEMA (read before editing):
 *  1. Never edit or delete an existing `this.version(n)` block — devices in
 *     the field have that version and still hold unsynced surveys.
 *  2. Add `this.version(n + 1).stores({ ...only the tables that change... })`
 *     and, if existing rows need reshaping, `.upgrade(async (tx) => { ... })`
 *     that rewrites them in place (e.g. `tx.table('drafts').toCollection()
 *     .modify((d) => { d.newField = ...; })`).
 *  3. Index strings: first entry is the primary key ('' = out-of-line key),
 *     then indexed properties. Non-indexed properties need no declaration.
 *  4. Add a test in db.test.ts that opens the previous version with data,
 *     then the new one, and checks the data survived.
 *  5. The outbox table holds unsent work: an upgrade must never clear it.
 *  6. Since v2 rows are encrypted (see sealing.ts). An upgrade that reshapes
 *     encrypted rows must decrypt/re-encrypt with the cipher, wrapping every
 *     WebCrypto call in `Dexie.waitFor(...)` — awaiting anything that isn't
 *     IndexedDB inside an upgrade otherwise ends the transaction early.
 *
 * Versions:
 *  v1 — plaintext tables.
 *  v2 — encryption at rest (ADR-0021): same indexes, sensitive fields moved
 *       into AES-GCM envelopes; the upgrade encrypts v1 rows in place.
 */
import Dexie, { type Table, type Transaction } from 'dexie';
import type { PackTemplateVersion } from '../api/types';
import type { Envelope, RecordCipher } from '../security/cipher';
import {
  sealAttachment,
  sealCustomer,
  sealDraft,
  sealFinding,
  sealMeta,
  sealOutbox,
  sealSite,
  type SealedAttachmentRow,
  type SealedDraftRow,
  type SealedFindingRow,
  type SealedOutboxRow,
  type SealedRow,
} from './sealing';

export const DEVICE_DB_NAME = 'sss-device';

export class DeviceDb extends Dexie {
  kv!: Table<Envelope, string>;
  sites!: Table<SealedRow, string>;
  customers!: Table<SealedRow, string>;
  templates!: Table<PackTemplateVersion, string>;
  findings!: Table<SealedFindingRow, string>;
  drafts!: Table<SealedDraftRow, string>;
  attachments!: Table<SealedAttachmentRow, string>;
  outbox!: Table<SealedOutboxRow, string>;

  /**
   * `cipher` is required whenever the database may need upgrading from v1
   * (i.e. always, in the app). Without it an upgrade fails closed rather than
   * leaving plaintext behind.
   */
  constructor(name = DEVICE_DB_NAME, cipher?: RecordCipher) {
    super(name);
    this.version(1).stores({
      kv: '',
      sites: 'id',
      customers: 'id',
      templates: 'versionId',
      findings: 'id, siteId',
      drafts: 'id, siteId, updatedAt',
      attachments: 'id, surveyId',
      outbox: 'id, entityId, seq',
    });
    // v2: no index changes; rows are encrypted in place.
    this.version(2)
      .stores({})
      .upgrade((tx) => {
        if (!cipher) throw new Error('Cannot upgrade the device database without the device key');
        return encryptV1Rows(tx, cipher);
      });
  }
}

/** v1 → v2: encrypt every sensitive row in place, in one atomic upgrade transaction. */
async function encryptV1Rows(tx: Transaction, c: RecordCipher): Promise<void> {
  const reseal = async <T, S>(table: string, seal: (row: T) => Promise<S>) => {
    const rows = (await tx.table(table).toArray()) as T[];
    if (rows.length === 0) return;
    const sealed = await Dexie.waitFor(Promise.all(rows.map(seal)));
    await tx.table(table).bulkPut(sealed);
  };
  await reseal('sites', (r: Parameters<typeof sealSite>[1]) => sealSite(c, r));
  await reseal('customers', (r: Parameters<typeof sealCustomer>[1]) => sealCustomer(c, r));
  await reseal('findings', (r: Parameters<typeof sealFinding>[1]) => sealFinding(c, r));
  await reseal('drafts', (r: Parameters<typeof sealDraft>[1]) => sealDraft(c, r));
  await reseal('attachments', (r: Parameters<typeof sealAttachment>[1]) => sealAttachment(c, r));
  // Same primary keys and seq values, so outbox order is unchanged.
  await reseal('outbox', (r: Parameters<typeof sealOutbox>[1]) => sealOutbox(c, r));

  const kv = tx.table('kv');
  const keys = (await kv.toCollection().primaryKeys()) as string[];
  if (keys.length) {
    const values = await kv.bulkGet(keys);
    const sealed = await Dexie.waitFor(Promise.all(keys.map((k, i) => sealMeta(c, k, values[i]))));
    await kv.bulkPut(sealed, keys);
  }
}

export const PACK_TABLES = ['sites', 'customers', 'templates', 'findings'] as const;
export const ALL_TABLES = ['kv', ...PACK_TABLES, 'drafts', 'attachments', 'outbox'] as const;
export type TableName = (typeof ALL_TABLES)[number];
